'use client';

import React, { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { ArrowRight, Zap, TrendingUp, Shield, Brain, Calendar, BookOpen, Target, CheckCircle, Clock, Users, Award, ChevronLeft, ChevronRight, Sparkles, Phone, GraduationCap, FileText } from 'lucide-react';
import CounsellingModal from '@/components/CounsellingModal';

// ============================================================
// BANNER DATA — 80% Admission Counselling, 20% NEET Predictor
// ============================================================
const BANNERS = [
  {
    id: 'b1',
    badge: "India\'s #1 NEET Admission Counselling Service",
    badgeColor: 'border-blue-300/40 text-[#1a3a8f] bg-blue-50/80',
    headline: 'Expert NEET Admission Counselling — Your Dream College Awaits',
    highlightWords: ['Expert NEET Admission Counselling'],
    sub: 'Personalised guidance from India\'s top NEET counsellors. AIQ, State quota, Deemed & NRI admissions — we handle every step of your journey.',
    cta1: { label: 'Book Free Counselling', href: '/student-dashboard', icon: Phone },
    cta2: { label: 'Explore Colleges', href: '/medical-colleges-database' },
    bgFrom: '#f0f4ff',
    bgTo: '#e8eeff',
    accentGlow: 'radial-gradient(ellipse 60% 50% at 70% 50%, rgba(26,58,143,0.15) 0%, transparent 70%)',
    image: '/assets/images/ChatGPT_Image_Jun_29__2026__04_58_29_PM-1782821404239.png',
    imageAlt: 'Friendly Indian counsellor mentor sitting with student reviewing college options on laptop with approval checkmarks and calendar icons',
    stats: [
      { icon: Users, value: '52,000+', label: 'Students Counselled' },
      { icon: Award, value: '98%', label: 'Satisfaction Rate' },
      { icon: GraduationCap, value: '500+', label: 'Expert Counsellors' },
    ],
    features: ['1-on-1 Expert Sessions', 'Choice Filling Guidance', 'Document Support'],
  },
  {
    id: 'b2',
    badge: 'AIQ & State Quota Counselling Specialists',
    badgeColor: 'border-blue-300/40 text-[#1a3a8f] bg-blue-50/80',
    headline: 'Navigate NEET Counselling with Confidence & Clarity',
    highlightWords: ['Confidence & Clarity'],
    sub: 'Our certified counsellors guide you through MCC AIQ rounds, state counselling, choice filling, document verification, and seat allotment — end to end.',
    cta1: { label: 'Talk to a Counsellor', href: '/student-dashboard', icon: Phone },
    cta2: { label: 'View Success Stories', href: '/#success-stories' },
    bgFrom: '#f0f4ff',
    bgTo: '#dbeafe',
    accentGlow: 'radial-gradient(ellipse 60% 50% at 70% 50%, rgba(26,58,143,0.15) 0%, transparent 70%)',
    image: '/assets/images/ChatGPT_Image_Jun_29__2026__04_56_47_PM-1782733919004.png',
    imageAlt: 'Indian medical student in white coat with AI-powered college prediction dashboard showing NEET admission data and university buildings',
    stats: [
      { icon: Shield, value: 'MCC', label: 'AIQ Experts' },
      { icon: Target, value: '28', label: 'States Covered' },
      { icon: CheckCircle, value: '4', label: 'Quota Types' },
    ],
    features: ['MCC AIQ Rounds', 'State Counselling', 'NRI & Deemed Quota'],
  },
  {
    id: 'b3',
    badge: '52,000+ Students Guided Successfully',
    badgeColor: 'border-blue-300/40 text-[#1a3a8f] bg-blue-50/80',
    headline: 'Personalised Admission Strategy Built for Your Rank',
    highlightWords: ['Personalised Admission Strategy'],
    sub: 'Every student gets a custom roadmap — college shortlisting, optimal choice filling order, round-wise strategy, and real-time counsellor support throughout.',
    cta1: { label: 'Get My Strategy', href: '/student-dashboard', icon: FileText },
    cta2: { label: 'Compare Colleges', href: '/compare-colleges' },
    bgFrom: '#f0f4ff',
    bgTo: '#e0f2fe',
    accentGlow: 'radial-gradient(ellipse 60% 50% at 70% 50%, rgba(26,58,143,0.15) 0%, transparent 70%)',
    image: '/assets/images/ChatGPT_Image_Jun_29__2026__04_56_51_PM-1782733926432.png',
    imageAlt: 'Modern dashboard with bar charts and rank distribution graphs showing NEET college cutoff data with Indian medical college buildings in background',
    stats: [
      { icon: Users, value: '52,000+', label: 'Students Guided' },
      { icon: CheckCircle, value: '98%', label: 'Satisfaction Rate' },
      { icon: Award, value: '7+', label: 'Years Experience' },
    ],
    features: ['College Shortlisting', 'Round-wise Strategy', 'Real-time Support'],
  },
  {
    id: 'b4',
    badge: 'Never Miss a Counselling Deadline',
    badgeColor: 'border-blue-300/40 text-[#1a3a8f] bg-blue-50/80',
    headline: 'Stay Ahead of Every Counselling Round & Deadline',
    highlightWords: ['Every Counselling Round'],
    sub: 'Track MCC AIQ rounds, state counselling schedules, registration windows, and seat allotment dates. Our counsellors alert you before every critical deadline.',
    cta1: { label: 'View Admission Calendar', href: '/admission-calendar', icon: Calendar },
    cta2: { label: 'Book Counsellor', href: '/student-dashboard' },
    bgFrom: '#f0f4ff',
    bgTo: '#fce7f3',
    accentGlow: 'radial-gradient(ellipse 60% 50% at 70% 50%, rgba(26,58,143,0.12) 0%, transparent 70%)',
    image: '/assets/images/ChatGPT_Image_Jun_29__2026__05_04_05_PM-1782733949412.png',
    imageAlt: 'Indian student checking admission calendar on phone with highlighted important dates, countdown timer, and counselling round notification cards',
    stats: [
      { icon: Calendar, value: '50+', label: 'Key Dates Tracked' },
      { icon: BookOpen, value: 'MCC + State', label: 'Counselling Rounds' },
      { icon: Sparkles, value: 'Live', label: 'Updates' },
    ],
    features: ['MCC AIQ Rounds', 'State Counselling', 'Registration Deadlines'],
  },
  {
    id: 'b5',
    badge: 'New Feature — AI-Powered NEET Predictor',
    badgeColor: 'border-blue-300/40 text-[#1a3a8f] bg-blue-50/80',
    headline: 'Predict Your College Before Counselling Begins',
    highlightWords: ['Predict Your College'],
    sub: 'Use our AI-powered NEET College Predictor to know which colleges you can get — then let our expert counsellors craft the perfect admission strategy for you.',
    cta1: { label: 'Try NEET Predictor', href: '/neet-college-predictor', icon: Brain },
    cta2: { label: 'Book Counselling', href: '/student-dashboard' },
    bgFrom: '#f0f4ff',
    bgTo: '#e8eeff',
    accentGlow: 'radial-gradient(ellipse 60% 50% at 70% 50%, rgba(26,58,143,0.15) 0%, transparent 70%)',
    image: '/assets/images/ChatGPT_Image_Jun_29__2026__05_02_41_PM-1782733942498.png',
    imageAlt: 'Excited Indian student looking at rank prediction results on tablet with glowing speedometer showing AIR rank numbers and percentile scores',
    stats: [
      { icon: Zap, value: '97%', label: 'Accuracy' },
      { icon: Clock, value: '<2 sec', label: 'Result Time' },
      { icon: TrendingUp, value: '600+', label: 'Colleges Covered' },
    ],
    features: ['AI College Prediction', 'Cutoff Analysis', 'Counsellor Follow-up'],
  },
];

// Highlight specific words in headline
function HighlightedHeadline({ text, words }: { text: string; words: string[] }) {
  const regex = new RegExp(`(${words.join('|')})`, 'gi');
  const splitParts = text.split(regex);

  return (
    <>
      {splitParts.map((part, i) => {
        const isHighlight = words.some((w) => w.toLowerCase() === part.toLowerCase());
        return isHighlight ? (
          <span key={i} className="hero-gradient-text">
            {part}
          </span>
        ) : (
          <span key={i}>{part}</span>
        );
      })}
    </>
  );
}

export default function HeroSection() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalSource, setModalSource] = useState('Hero Banner');

  const goTo = useCallback(
    (index: number) => {
      if (isTransitioning) return;
      setIsTransitioning(true);
      setTimeout(() => {
        setActiveIndex(index);
        setIsTransitioning(false);
      }, 350);
    },
    [isTransitioning]
  );

  const goNext = useCallback(() => {
    goTo((activeIndex + 1) % BANNERS.length);
  }, [activeIndex, goTo]);

  const goPrev = useCallback(() => {
    goTo((activeIndex - 1 + BANNERS.length) % BANNERS.length);
  }, [activeIndex, goTo]);

  useEffect(() => {
    if (isPaused) return;
    const timer = setInterval(goNext, 7000);
    return () => clearInterval(timer);
  }, [goNext, isPaused]);

  const banner = BANNERS[activeIndex];
  const Cta1Icon = banner.cta1.icon;

  const COUNSELLING_LABELS = ['Book Free Counselling', 'Talk to a Counsellor', 'Book Counsellor', 'Book Counselling'];

  const isCounsellingCta1 = COUNSELLING_LABELS.includes(banner.cta1.label);
  const isCounsellingCta2 = COUNSELLING_LABELS.includes(banner.cta2.label);

  const handleCta1Click = (e: React.MouseEvent) => {
    if (isCounsellingCta1) {
      e.preventDefault();
      setModalSource(banner.cta1.label);
      setModalOpen(true);
    }
  };

  const handleCta2Click = (e: React.MouseEvent) => {
    if (isCounsellingCta2) {
      e.preventDefault();
      setModalSource(banner.cta2.label);
      setModalOpen(true);
    }
  };

  return (
    <>
    <CounsellingModal isOpen={modalOpen} onClose={() => setModalOpen(false)} source={modalSource} />
    <section
      className="relative min-h-screen flex items-center justify-center pt-16 overflow-hidden"
      style={{
        background: `linear-gradient(135deg, ${banner.bgFrom} 0%, ${banner.bgTo} 100%)`,
        transition: 'background 0.6s ease',
      }}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {/* Accent glow */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: banner.accentGlow, transition: 'background 0.6s ease' }}
      />
      {/* Subtle grid overlay */}
      <div className="absolute inset-0 grid-overlay pointer-events-none opacity-40" />

      {/* Full-width slide layout */}
      <div
        className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-28"
        style={{
          opacity: isTransitioning ? 0 : 1,
          transform: isTransitioning ? 'translateY(16px)' : 'translateY(0)',
          transition: 'opacity 0.35s ease, transform 0.35s ease',
        }}
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* LEFT: Text content */}
          <div className="flex flex-col items-start">
            {/* Badge */}
            <div
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border mb-6 shadow-sm ${banner.badgeColor}`}
            >
              <Zap size={13} className="shrink-0" />
              <span>{banner.badge}</span>
              <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />
            </div>

            {/* Headline */}
            <h1 className="text-[38px] font-extrabold tracking-tight mb-5 text-foreground">
              <HighlightedHeadline text={banner.headline} words={banner.highlightWords} />
            </h1>

            {/* Sub */}
            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed mb-8 max-w-xl">
              {banner.sub}
            </p>

            {/* Feature pills */}
            <div className="flex flex-wrap gap-2 mb-8">
              {banner.features.map((f, i) => (
                <span
                  key={i}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/80 border border-[#1a3a8f]/15 rounded-full text-xs font-semibold text-foreground shadow-sm"
                >
                  <CheckCircle size={11} className="text-[#1a3a8f]" />
                  {f}
                </span>
              ))}
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-3 mb-10 w-full sm:w-auto">
              <Link
                href={banner.cta1.href}
                onClick={handleCta1Click}
                className="btn-primary flex items-center justify-center gap-2 px-7 py-4 text-base rounded-2xl"
              >
                <Cta1Icon size={18} />
                {banner.cta1.label}
                <ArrowRight size={16} />
              </Link>
              <Link
                href={banner.cta2.href}
                onClick={handleCta2Click}
                className="btn-secondary flex items-center justify-center gap-2 px-7 py-4 text-base rounded-2xl"
              >
                {banner.cta2.label}
              </Link>
            </div>

            {/* Stats row */}
            <div className="flex flex-wrap gap-5">
              {banner.stats.map((stat, i) => (
                <div key={i} className="flex items-center gap-2">
                  <div className="w-9 h-9 rounded-xl bg-[#1a3a8f]/8 flex items-center justify-center">
                    <stat.icon size={16} className="text-[#1a3a8f]" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-foreground leading-none">{stat.value}</p>
                    <p className="text-xs text-muted-foreground">{stat.label}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT: Banner image */}
          <div className="relative flex items-center justify-center">
            <div className="relative w-full max-w-lg aspect-[4/3] rounded-3xl overflow-hidden shadow-2xl shadow-[#0a1f5c]/15">
              <Image
                src={banner.image}
                alt={banner.imageAlt}
                fill
                className="object-cover"
                priority={activeIndex === 0}
                sizes="(max-width: 768px) 100vw, 50vw"
              />
              {/* Overlay gradient */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a1f5c]/20 via-transparent to-transparent" />
            </div>

            {/* Floating trust badge */}
            <div className="absolute -bottom-4 -left-4 bg-white rounded-2xl shadow-xl px-4 py-3 flex items-center gap-2.5 border border-[#1a3a8f]/10">
              <div className="w-8 h-8 rounded-xl bg-[#1a3a8f] flex items-center justify-center">
                <Shield size={14} className="text-white" />
              </div>
              <div>
                <p className="text-xs font-bold text-foreground">Trusted Counsellors</p>
                <p className="text-[10px] text-muted-foreground">Since 2019</p>
              </div>
            </div>
          </div>
        </div>

        {/* Slide controls */}
        <div className="flex items-center justify-center gap-4 mt-12">
          <button
            onClick={goPrev}
            className="w-9 h-9 rounded-full bg-white/80 border border-[#1a3a8f]/15 flex items-center justify-center text-[#1a3a8f] hover:bg-white transition-all shadow-sm"
            aria-label="Previous slide"
          >
            <ChevronLeft size={16} />
          </button>
          <div className="flex items-center gap-2">
            {BANNERS.map((_, i) => (
              <button
                key={i}
                onClick={() => goTo(i)}
                className={`rounded-full transition-all duration-300 ${
                  i === activeIndex ? 'w-6 h-2 bg-[#1a3a8f]' : 'w-2 h-2 bg-[#1a3a8f]/25 hover:bg-[#1a3a8f]/50'
                }`}
                aria-label={`Go to slide ${i + 1}`}
              />
            ))}
          </div>
          <button
            onClick={goNext}
            className="w-9 h-9 rounded-full bg-white/80 border border-[#1a3a8f]/15 flex items-center justify-center text-[#1a3a8f] hover:bg-white transition-all shadow-sm"
            aria-label="Next slide"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>
    </section>
    </>
  );
}